import { useCallback, useEffect, useState } from "react";
import { useSession } from "@/auth/SessionProvider";
import { resolveKnowledgeOrgId } from "@/lib/knowledgeOrgId";
import {
  KNOWLEDGE_CUSTOM_BOOKS_CHANGED,
  listCustomBooks,
  type KnowledgeCustomBook,
} from "@/lib/knowledgeBookRegistry";
import { apiCreateKnowledgeBook } from "@/lib/knowledgeRegistryApi";
import { refreshKnowledgeRegistryFromApi } from "@/lib/knowledgeRegistryCrossSync";
import { useKnowledgeBases } from "@/pages/knowledge/useKnowledgeBases";

export function useCustomBooks() {
  const session = useSession();
  const orgId = resolveKnowledgeOrgId(session.data?.user?.orgId);
  const { activeId } = useKnowledgeBases();
  const [books, setBooks] = useState<KnowledgeCustomBook[]>(() => listCustomBooks(orgId, activeId));

  const reloadLocal = useCallback(() => {
    setBooks(listCustomBooks(orgId, activeId));
  }, [orgId, activeId]);

  const reload = useCallback(async () => {
    if (orgId) await refreshKnowledgeRegistryFromApi(orgId);
    reloadLocal();
  }, [orgId, reloadLocal]);

  useEffect(() => {
    void reload();
  }, [reload]);

  useEffect(() => {
    function onChange() {
      reloadLocal();
    }
    window.addEventListener(KNOWLEDGE_CUSTOM_BOOKS_CHANGED, onChange);
    window.addEventListener("storage", onChange);
    return () => {
      window.removeEventListener(KNOWLEDGE_CUSTOM_BOOKS_CHANGED, onChange);
      window.removeEventListener("storage", onChange);
    };
  }, [reloadLocal]);

  const createBook = useCallback(
    async (input: {
      title: string;
      description?: string;
      collectionKey?: string;
      libraryId?: string;
    }) => {
      if (!(activeId || "").trim()) {
        return { ok: false as const, error: "Select or create a knowledge base first." };
      }
      const res = await apiCreateKnowledgeBook(activeId, input);
      if (!res.ok || !res.data?.book) {
        return { ok: false as const, error: res.error || "Could not create book." };
      }
      if (orgId) await refreshKnowledgeRegistryFromApi(orgId);
      reloadLocal();
      return { ok: true as const, book: res.data.book };
    },
    [orgId, activeId, reloadLocal],
  );

  return { books, orgId, kbId: activeId, createBook, reload };
}
